import { useLayoutEffect } from 'react';

// The app banner (CMS announcement) is pinned to the top on desktop and publishes its height
const bannerHeight = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--mt-banner-h')) || 0;

/**
 * A desktop column that stays in view while the page scrolls, with no scrollbar of its own (owner rule). It's
 * position: sticky; when it's taller than the screen it moves with the page until its other end shows, in either
 * direction, then holds there (the sidebar pattern). A new resetKey brings its top back into view, e.g. when another
 * exercise opens while the list is scrolled far down. gap: the room kept above it, under the app banner if any
 */
export const useStickyColumn = (ref, { enabled, gap, resetKey }) => {
  useLayoutEffect(() => {
    const column = ref.current;
    if (!enabled || !column) return undefined;
    const highest = () => gap + bannerHeight();
    // The lowest it may sit: its bottom `gap` above the bottom of the screen
    const lowest = () => Math.min(highest(), window.innerHeight - column.offsetHeight - gap);
    let top = highest();
    let lastY = window.scrollY;
    const apply = () => {
      column.style.top = `${Math.round(top)}px`;
    };
    const handleScroll = () => {
      const y = window.scrollY;
      top = Math.max(lowest(), Math.min(highest(), top - (y - lastY)));
      lastY = y;
      apply();
    };
    const handleResize = () => {
      top = Math.max(lowest(), Math.min(highest(), top));
      apply();
    };

    apply();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(handleResize) : null;
    observer?.observe(column);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      observer?.disconnect();
      column.style.top = '';
    };
  }, [ref, enabled, gap, resetKey]);
};
